import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'


import { useAuth } from '@features/auth'

export type FormFieldFixedAnswer = {
  name: string
  value: string
}

export type FormFieldRemote = {
  id: string
  group: string
  order: number
  label: string
  fieldName: string
  type: string
  required: boolean
  isReadOnly: boolean
  isVisibleInRegistration: boolean
  isVisibleInProfile: boolean
  answer?: string | null
  fixedAnswers: FormFieldFixedAnswer[]
  defaultValue?: string | null
  payload?: string | null
  visibleWhen?: {
    fieldName: string
    equals: string
  } | null
  isAutoComplete?: boolean
}

export type FormField = Omit<FormFieldRemote, 'payload'> & {
  payload?: Record<string, unknown>
}

type Query = {
  customerProfileFields: FormFieldRemote[]
}

type Variables = {
  isRegistration?: boolean
}

export const GQL_CUSTOMER_PROFILE_FIELDS = gql`
  query CustomerProfileFields($isRegistration: Boolean) {
    customerProfileFields(isRegistration: $isRegistration) {
      id
      order
      group
      label
      fieldName
      type
      required
      isReadOnly
      isVisibleInRegistration
      isVisibleInProfile
      answer
      defaultValue
      fixedAnswers {
        name
        value
      }
      payload
      visibleWhen {
        fieldName
        equals
      }
      isAutoComplete
    }
  }
`

export const useCustomerProfileFields = (isRegistration?: boolean) => {
  const { user } = useAuth()

  const { data, loading, error, refetch } = useQuery<Query, Variables>(
    GQL_CUSTOMER_PROFILE_FIELDS,
    {
      variables: {
        isRegistration: !!isRegistration,
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
      skip: !user,
    },
  )

  const fields = useMemo(() => data?.customerProfileFields
    ? data?.customerProfileFields?.filter((field) => {
      // Filter out fields that are not visible in registration if isRegistration is false
      if (isRegistration) {
        return field.isVisibleInRegistration
      }
      return field.isVisibleInProfile
    })
    : null, [ data?.customerProfileFields, isRegistration ])

  const normalizedFields: FormField[] | undefined = useMemo(() => fields?.map((field) => {
    const fieldName = field.fieldName.split('.').join('__')

    const payload = field.payload ? JSON.parse(field.payload) : {}

    // Нормализуем fieldName в visibleWhen (точки -> __)
    const visibleWhen = field.visibleWhen
      ? {
        ...field.visibleWhen,
        fieldName: field.visibleWhen.fieldName.split('.').join('__'),
      }
      : undefined

    return {
      ...field,
      payload,
      fieldName,
      visibleWhen,
    }
  }), [ fields ])

  const fieldsByGroup = useMemo(() => normalizedFields?.reduce(
    (acc, field) => {
      const group = field.group || 'default'
      if (!acc[group]) {
        acc[group] = []
      }
      acc[group].push(field)
      return acc
    },
    {} as Record<string, FormField[]>,
  ), [ normalizedFields ])

  return {
    fieldsByGroup,
    fields,
    normalizedFields,
    data,
    loading,
    error,
    refetch,
  }
}
